import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Printer, Loader2, Search, Phone, Stethoscope } from 'lucide-react';
import { toast } from 'sonner';
import { openPrintWindow } from '@/lib/letterhead';

export default function ContattiTab({ area }) {
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const fetchDati = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/moduli/contatti', { params: { area } });
      setDati(r.data);
    } catch { toast.error('Errore caricamento rubrica'); }
    setLoading(false);
  }, [area]);

  useEffect(() => { fetchDati(); }, [fetchDati]);

  const righe = (dati?.ospiti || []).filter(o => o.nome_completo.toLowerCase().includes(q.toLowerCase()));

  const stampa = () => {
    let html = `<table><thead><tr><th>Ospite</th><th>Stanza</th><th>Referenti familiari</th><th>Medico di base</th></tr></thead><tbody>`;
    righe.forEach(o => {
      html += `<tr><td>${o.nome_completo}</td><td>${o.stanza || '-'}${o.letto ? '/' + o.letto : ''}</td>
        <td>${o.referenti.map(r => `${r.nome}${r.parentela ? ` (${r.parentela})` : ''} - ${r.telefono}`).join('<br/>') || '-'}</td>
        <td>${o.medico_base || '-'}</td></tr>`;
    });
    html += '</tbody></table>';
    openPrintWindow('Rubrica Contatti Ospiti', html, { subtitle: `${righe.length} ospiti · area ${area || 'tutte'}` });
  };

  if (loading && !dati) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-4" data-testid="contatti-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Cerca ospite"
            className="h-9 pl-8 w-52" data-testid="contatti-search" />
        </div>
        <Badge variant="outline" className="text-[11px]">{righe.length} ospiti</Badge>
        {dati?.senza_contatti > 0 && (
          <Badge className="bg-amber-100 text-amber-700 text-[11px]">{dati.senza_contatti} senza contatti</Badge>
        )}
        <Button size="sm" variant="outline" onClick={stampa} className="h-9" data-testid="contatti-print">
          <Printer className="w-4 h-4 mr-1" />Stampa rubrica
        </Button>
      </div>

      <div className="grid gap-2 md:grid-cols-2">
        {righe.map(o => (
          <Card key={o.id} className={o.senza_contatti ? 'border-amber-200' : ''} data-testid={`contatto-${o.id}`}>
            <CardContent className="p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{o.nome_completo}</p>
                  <p className="text-[11px] text-slate-400">
                    Stanza {o.stanza || '-'}{o.letto ? ` letto ${o.letto}` : ''}{o.area ? ` · area ${o.area}` : ''}
                  </p>
                </div>
                {o.medico_base && (
                  <span className="text-[10px] text-slate-500 flex items-center gap-1 flex-shrink-0">
                    <Stethoscope className="w-3 h-3" />{o.medico_base}
                  </span>
                )}
              </div>
              <div className="mt-2 space-y-1">
                {o.referenti.length === 0 && <p className="text-[11px] text-amber-600">Nessun contatto registrato</p>}
                {o.referenti.map((r, i) => (
                  <a key={i} href={`tel:${r.telefono}`}
                    className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-slate-50 hover:bg-brand-soft transition-colors">
                    <span className="text-xs text-slate-700 truncate">
                      {r.nome}{r.parentela ? <span className="text-slate-400"> · {r.parentela}</span> : null}
                    </span>
                    <span className="text-xs font-semibold text-brand-dark flex items-center gap-1 flex-shrink-0">
                      <Phone className="w-3 h-3" />{r.telefono}
                    </span>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
